import { useState, useCallback } from "react";

// ── VEHICLE DATA ──────────────────────────────────────────────────────────────
const VEHICLE_DATA = {
  "Acura":         ["ILX","Integra","MDX","RDX","TLX","ZDX"],
  "Audi":          ["A3","A4","A5","A6","A7","A8","e-tron","Q3","Q5","Q7","Q8","TT"],
  "BMW":           ["2 Series","3 Series","4 Series","5 Series","7 Series","8 Series","i4","iX","X1","X2","X3","X4","X5","X6","X7","Z4"],
  "Buick":         ["Enclave","Encore","Encore GX","Envision"],
  "Cadillac":      ["CT4","CT5","Escalade","LYRIQ","XT4","XT5","XT6"],
  "Chevrolet":     ["Blazer","Bolt EV","Colorado","Corvette","Equinox","Malibu","Silverado 1500","Silverado 2500HD","Suburban","Tahoe","Traverse","Trax"],
  "Chrysler":      ["300","Pacifica","Voyager"],
  "Dodge":         ["Challenger","Charger","Durango","Hornet","Ram 1500"],
  "Ford":          ["Bronco","Bronco Sport","Edge","Escape","Explorer","F-150","F-250","F-350","Maverick","Mustang","Mustang Mach-E","Ranger","Transit"],
  "GMC":           ["Acadia","Canyon","Sierra 1500","Sierra 2500HD","Terrain","Yukon"],
  "Honda":         ["Accord","Civic","CR-V","HR-V","Odyssey","Passport","Pilot","Ridgeline"],
  "Hyundai":       ["Elantra","IONIQ 5","IONIQ 6","Kona","Palisade","Santa Fe","Sonata","Tucson","Venue"],
  "Infiniti":      ["Q50","Q60","QX50","QX55","QX60","QX80"],
  "Jeep":          ["Cherokee","Compass","Gladiator","Grand Cherokee","Renegade","Wrangler"],
  "Kia":           ["Carnival","EV6","Forte","K5","Niro","Seltos","Sorento","Soul","Sportage","Telluride"],
  "Land Rover":    ["Defender","Discovery","Discovery Sport","Range Rover","Range Rover Evoque","Range Rover Sport"],
  "Lexus":         ["ES","GS","GX","IS","LC","LS","LX","NX","RX","UX"],
  "Lincoln":       ["Aviator","Corsair","Nautilus","Navigator"],
  "Mazda":         ["CX-30","CX-5","CX-50","CX-90","Mazda3","Mazda6","MX-5 Miata"],
  "Mercedes-Benz": ["A-Class","C-Class","CLA","E-Class","EQS","G-Class","GLA","GLC","GLE","GLS","S-Class"],
  "Mitsubishi":    ["Eclipse Cross","Outlander","Outlander Sport"],
  "Nissan":        ["Altima","Armada","Frontier","Kicks","Leaf","Maxima","Murano","Pathfinder","Rogue","Sentra","Titan","Versa"],
  "Ram":           ["1500","2500","3500","ProMaster"],
  "Rivian":        ["R1S","R1T"],
  "Subaru":        ["Ascent","BRZ","Crosstrek","Forester","Impreza","Legacy","Outback","Solterra","WRX"],
  "Tesla":         ["Cybertruck","Model 3","Model S","Model X","Model Y"],
  "Toyota":        ["4Runner","Camry","Corolla","Crown","GR86","Highlander","Land Cruiser","Prius","RAV4","Sequoia","Sienna","Tacoma","Tundra","Venza","bZ4X"],
  "Volkswagen":    ["Atlas","Golf","ID.4","Jetta","Passat","Taos","Tiguan"],
  "Volvo":         ["C40","S60","S90","V60","XC40","XC60","XC90"],
};

const VEHICLE_MAKES = Object.keys(VEHICLE_DATA).sort();
const VEHICLE_YEARS = Array.from({ length: 31 }, (_, i) => String(2025 - i));

// ── COMMON PROBLEMS ───────────────────────────────────────────────────────────
const PROBLEM_CATEGORIES = [
  {
    category: "Warning Lights",
    icon: "⚠️",
    problems: [
      "Tire Pressure Warning (TPMS)",
      "Check Engine Light",
      "Battery Warning Light",
      "Oil Pressure Warning",
      "ABS Warning Light",
      "Brake Warning Light",
      "Airbag / SRS Warning",
      "Transmission Warning",
    ],
  },
  {
    category: "Tires & Wheels",
    icon: "🔧",
    problems: [
      "Flat Tire / Puncture",
      "Tire Rotation",
      "Wheel Alignment",
      "Wheel Balancing",
      "Run-flat Tire Replacement",
    ],
  },
  {
    category: "Brakes",
    icon: "🛑",
    problems: [
      "Squeaking / Squealing Brakes",
      "Grinding Brakes",
      "Soft Brake Pedal",
      "Brake Pad Replacement",
      "Brake Rotor Replacement",
    ],
  },
  {
    category: "Engine & Oil",
    icon: "🛢️",
    problems: [
      "Oil Change",
      "Engine Overheating",
      "Engine Misfiring",
      "Rough Idle",
      "Car Won't Start",
      "Coolant Leak",
    ],
  },
  {
    category: "Electrical & Battery",
    icon: "⚡",
    problems: [
      "Dead Battery",
      "Battery Replacement",
      "Alternator Issue",
      "Starter Motor Problem",
      "Electrical Short",
    ],
  },
  {
    category: "Transmission",
    icon: "⚙️",
    problems: [
      "Transmission Slipping",
      "Rough Shifting",
      "Transmission Fluid Change",
      "Clutch Replacement",
    ],
  },
  {
    category: "AC & Heating",
    icon: "❄️",
    problems: [
      "AC Not Cooling",
      "AC Refrigerant Recharge",
      "Heater Not Working",
      "Blower Motor Issue",
    ],
  },
];

// ── PARTS AFFILIATE URLS ──────────────────────────────────────────────────────
function getPartsUrls(year, make, model, problem) {
  const query = encodeURIComponent(`${year} ${make} ${model} ${problem}`);
  const partQuery = encodeURIComponent(problem);
  return [
    {
      name: "AutoZone",
      color: "#e4000f",
      icon: "A",
      url: `https://www.autozone.com/searchresult?searchText=${query}`,
    },
    {
      name: "O'Reilly Auto",
      color: "#00843d",
      icon: "O",
      url: `https://www.oreillyauto.com/search?q=${query}`,
    },
    {
      name: "RockAuto",
      color: "#cc0000",
      icon: "R",
      url: `https://www.rockauto.com/en/catalog/${make.toLowerCase()},${year}`,
    },
    {
      name: "Amazon Auto",
      color: "#ff9900",
      icon: "Z",
      url: `https://www.amazon.com/s?k=${query}&rh=n%3A15684181&tag=mioprice-20`,
    },
    {
      name: "Advance Auto",
      color: "#e4000f",
      icon: "V",
      url: `https://shop.advanceautoparts.com/find/search.htm?searchTerm=${query}`,
    },
  ];
}

// ── AI PROMPT — PRICE ESTIMATOR ───────────────────────────────────────────────
function pricePrompt(year, make, model, problem) {
  return `Return ONLY raw JSON for repair cost options for: ${year} ${make} ${model} — Problem: "${problem}"

Show the full cost hierarchy from cheapest to most expensive.

Schema:
{"results":[
  {
    "option":"DIY Fix",
    "cost_low":0,
    "cost_high":0,
    "time_estimate":"30 min",
    "difficulty":"Easy",
    "description":"",
    "steps":[],
    "parts_needed":[],
    "tools_needed":[],
    "warning":"",
    "recommended":false,
    "save_vs_dealer":0
  }
]}

Include ALL of these options in order (cheapest first):
1. "DIY Fix" — do it yourself, parts only cost. difficulty: Easy/Medium/Hard/Expert
2. "Quick Fix / Temporary" — if applicable (e.g. tire inflator, reset procedure)
3. "Budget Shop" — Costco Auto, Walmart Auto Center, Firestone, Midas, Meineke
4. "Independent Mechanic" — local shop via RepairPal/YourMechanic estimate
5. "Dealership" — manufacturer dealer estimate

Rules:
- Be SPECIFIC to ${year} ${make} ${model} — some repairs are model-specific
- cost_low and cost_high are realistic ranges in USD (parts + labor where applicable)
- DIY cost_low/high = parts only, no labor
- steps: 3-5 step DIY instructions if DIY is feasible
- parts_needed: specific part names for this vehicle
- tools_needed: tools required for DIY
- warning: safety warning if applicable (e.g. "Do not drive with grinding brakes")
- recommended: true for the best value option
- save_vs_dealer: dollar savings vs dealership price
- If problem is a warning light reset (like TPMS), DIY cost should be $0`;
}

// ── API CALLS ─────────────────────────────────────────────────────────────────
async function callAgent(prompt) {
  const resp = await fetch("/api/anthropic", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 4000,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!resp.ok) throw new Error(`API error ${resp.status}`);
  const data = await resp.json();
  const text = data.content?.find(b => b.type === "text")?.text || "";
  const f = text.indexOf("{"), l = text.lastIndexOf("}");
  if (f === -1) throw new Error("No JSON in response");
  return JSON.parse(text.slice(f, l + 1));
}

async function searchYouTube(year, make, model, problem) {
  const q = encodeURIComponent(`${year} ${make} ${model} ${problem} fix how to`);
  const resp = await fetch(`/api/youtube?q=${q}&maxResults=6`);
  if (!resp.ok) throw new Error(`YouTube API error ${resp.status}`);
  return resp.json();
}

async function fetchRecalls(year, make, model) {
  const url = `https://api.nhtsa.gov/recalls/recallsByVehicle?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&modelYear=${year}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`NHTSA API error ${resp.status}`);
  return resp.json();
}

// ── UI HELPERS ────────────────────────────────────────────────────────────────
const inp = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: 14, outline: "none", boxSizing: "border-box", background: "#fff" };
const lbl = { fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: 0.5 };
const fld = { display: "flex", flexDirection: "column" };

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <span style={{ fontSize: 22 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "#6b7280" }}>{subtitle}</div>}
      </div>
    </div>
  );
}

// ── PRICE ESTIMATE CARD ───────────────────────────────────────────────────────
function PriceCard({ r, rank }) {
  const [open, setOpen] = useState(rank === 0);

  const optionColors = {
    "DIY Fix":              { bg: "#f0fdf4", border: "#bbf7d0", badge: "#15803d", badgeBg: "#dcfce7" },
    "Quick Fix / Temporary":{ bg: "#f0fdf4", border: "#bbf7d0", badge: "#15803d", badgeBg: "#dcfce7" },
    "Budget Shop":          { bg: "#eff6ff", border: "#bfdbfe", badge: "#1d4ed8", badgeBg: "#dbeafe" },
    "Independent Mechanic": { bg: "#fffbeb", border: "#fde68a", badge: "#b45309", badgeBg: "#fef3c7" },
    "Dealership":           { bg: "#fef2f2", border: "#fecaca", badge: "#dc2626", badgeBg: "#fee2e2" },
  };
  const difficultyColor = { Easy: "#15803d", Medium: "#d97706", Hard: "#dc2626", Expert: "#7c3aed" };
  const c = optionColors[r.option] || optionColors["Budget Shop"];
  const isFree = r.cost_high === 0;

  return (
    <div style={{ borderRadius: 14, marginBottom: 10, overflow: "hidden", border: `1.5px solid ${r.recommended ? "#2563eb" : c.border}`, background: "#fff", boxShadow: r.recommended ? "0 2px 12px rgba(37,99,235,0.10)" : "0 1px 3px rgba(0,0,0,0.04)" }}>
      {r.recommended && <div style={{ background: "#eff6ff", borderBottom: "1px solid #bfdbfe", padding: "5px 18px", display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb" }} /><span style={{ fontSize: 11, fontWeight: 600, color: "#1d4ed8" }}>Best value option</span></div>}
      <div onClick={() => setOpen(o => !o)} style={{ padding: "14px 18px", cursor: "pointer" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>{r.option}</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: c.badgeBg, color: c.badge }}>
                {rank === 0 ? "Cheapest" : rank === 4 ? "Most expensive" : `Option ${rank + 1}`}
              </span>
              {r.difficulty && <span style={{ fontSize: 11, fontWeight: 600, color: difficultyColor[r.difficulty] || "#6b7280" }}>{r.difficulty}</span>}
            </div>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>{r.description}</div>
            {r.warning && <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626", display: "flex", alignItems: "center", gap: 4 }}><span>⚠️</span>{r.warning}</div>}
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            {isFree ? (
              <div style={{ fontSize: 26, fontWeight: 900, color: "#15803d" }}>FREE</div>
            ) : (
              <>
                <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -1 }}>${r.cost_low?.toLocaleString()}{r.cost_high > r.cost_low ? `–$${r.cost_high?.toLocaleString()}` : ""}</div>
                {r.save_vs_dealer > 0 && <div style={{ fontSize: 11, color: "#15803d", marginTop: 2 }}>Save ${r.save_vs_dealer?.toLocaleString()} vs dealer</div>}
              </>
            )}
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{r.time_estimate}</div>
          </div>
        </div>

        {open && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
            {r.steps?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>DIY Steps</div>
                {r.steps.map((step, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, marginBottom: 6 }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#1d4ed8", color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                    <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>{step}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              {r.parts_needed?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Parts needed</div>
                  {r.parts_needed.map((p, i) => <div key={i} style={{ fontSize: 12, color: "#374151" }}>• {p}</div>)}
                </div>
              )}
              {r.tools_needed?.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Tools needed</div>
                  {r.tools_needed.map((t, i) => <div key={i} style={{ fontSize: 12, color: "#374151" }}>• {t}</div>)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── YOUTUBE VIDEO CARD ────────────────────────────────────────────────────────
function VideoCard({ video }) {
  const { snippet } = video;
  const videoId = video.id?.videoId;
  const thumb = snippet?.thumbnails?.medium?.url || snippet?.thumbnails?.default?.url;
  const channel = snippet?.channelTitle;
  const title = snippet?.title;
  const date = snippet?.publishedAt ? new Date(snippet.publishedAt).getFullYear() : "";

  return (
    <a href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noopener noreferrer"
      style={{ display: "flex", gap: 12, padding: "12px", borderRadius: 12, border: "1.5px solid #e5e7eb", background: "#fff", textDecoration: "none", marginBottom: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "#2563eb"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "#e5e7eb"}>
      <div style={{ position: "relative", flexShrink: 0 }}>
        {thumb && <img src={thumb} alt={title} style={{ width: 140, height: 79, objectFit: "cover", borderRadius: 8 }} />}
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 12, color: "#fff", marginLeft: 2 }}>▶</span>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#111", lineHeight: 1.4, marginBottom: 4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{title}</div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>{channel} {date ? `· ${date}` : ""}</div>
        <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: "#dc2626", background: "#fff1f2", padding: "2px 8px", borderRadius: 20, display: "inline-block" }}>▶ Watch on YouTube</div>
      </div>
    </a>
  );
}

// ── RECALL CARD ───────────────────────────────────────────────────────────────
function RecallCard({ recall }) {
  const [open, setOpen] = useState(false);
  return (
    <div onClick={() => setOpen(o => !o)} style={{ borderRadius: 12, marginBottom: 8, padding: "12px 16px", border: "1.5px solid #fecaca", background: "#fff", cursor: "pointer" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 2 }}>⚠️ {recall.Component}</div>
          <div style={{ fontSize: 12, color: "#374151" }}>{recall.Summary?.slice(0, 120)}{recall.Summary?.length > 120 ? "..." : ""}</div>
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>{recall.ReportReceivedDate?.slice(0, 10)}</div>
      </div>
      {open && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #fecaca" }}>
          <div style={{ fontSize: 12, color: "#374151", marginBottom: 8, lineHeight: 1.6 }}>{recall.Consequence}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d", marginBottom: 4 }}>Remedy:</div>
          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>{recall.Remedy}</div>
          {recall.NHTSACampaignNumber && (
            <a href={`https://www.nhtsa.gov/recalls?nhtsaId=${recall.NHTSACampaignNumber}`} target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ display: "inline-block", marginTop: 8, fontSize: 11, color: "#1d4ed8", textDecoration: "underline" }}>
              NHTSA #{recall.NHTSACampaignNumber} →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

// ── PROBLEM ACCORDION ────────────────────────────────────────────────────────
function ProblemAccordion({ problems, setProblems }) {
  const [openIdx, setOpenIdx] = useState(null);

  const toggle = (i) => setOpenIdx(prev => prev === i ? null : i);

  const toggleProblem = (p) => {
    setProblems(prev => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  const allCategories = [
    ...PROBLEM_CATEGORIES,
    { category: "Other", icon: "✏️", problems: ["Other"] },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 4 }}>
      {allCategories.map((cat, i) => {
        const isOpen      = openIdx === i;
        const selectedInCat = cat.problems.filter(p => problems.has(p));
        const hasSelected   = selectedInCat.length > 0;
        return (
          <div key={cat.category} style={{ borderRadius: 12, overflow: "hidden", border: `1.5px solid ${hasSelected ? "#2563eb" : isOpen ? "#e2e8f0" : "#e5e7eb"}` }}>
            {/* Header row */}
            <button onClick={() => toggle(i)} style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "11px 14px", background: hasSelected ? "#eff6ff" : isOpen ? "#f8fafc" : "#fff",
              border: "none", cursor: "pointer", textAlign: "left",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 16 }}>{cat.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: hasSelected ? "#1d4ed8" : "#374151" }}>
                  {cat.category}
                </span>
                {selectedInCat.map(p => (
                  <span key={p} style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", background: "#dbeafe", padding: "1px 8px", borderRadius: 20 }}>
                    {p === "Other" ? "Custom" : p}
                  </span>
                ))}
              </div>
              <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600, flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</span>
            </button>

            {/* Expanded pills — stay open for multi-select */}
            {isOpen && cat.category !== "Other" && (
              <div style={{ padding: "10px 14px 12px", background: "#fafafa", borderTop: "1px solid #f1f5f9", display: "flex", flexWrap: "wrap", gap: 6 }}>
                {cat.problems.map(p => {
                  const sel = problems.has(p);
                  return (
                    <button key={p} onClick={() => toggleProblem(p)}
                      style={{ padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${sel ? "#2563eb" : "#e5e7eb"}`, background: sel ? "#eff6ff" : "#fff", color: sel ? "#1d4ed8" : "#374151", fontSize: 12, fontWeight: sel ? 700 : 400, cursor: "pointer" }}>
                      {sel ? "✓ " : ""}{p}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Other expanded */}
            {isOpen && cat.category === "Other" && (
              <div style={{ padding: "10px 14px 12px", background: "#fafafa", borderTop: "1px solid #f1f5f9" }}>
                <button onClick={() => toggleProblem("Other")}
                  style={{ padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${problems.has("Other") ? "#2563eb" : "#e5e7eb"}`, background: problems.has("Other") ? "#eff6ff" : "#fff", color: problems.has("Other") ? "#1d4ed8" : "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {problems.has("Other") ? "✓ " : ""}Describe my problem
                </button>
              </div>
            )}
          </div>
        );
      })}
      {problems.size > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 2px" }}>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{problems.size} problem{problems.size > 1 ? "s" : ""} selected</div>
          <button onClick={() => setProblems(new Set())} style={{ fontSize: 11, color: "#dc2626", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear all</button>
        </div>
      )}
    </div>
  );
}

// ── RECALL BANNER (compact collapsible) ──────────────────────────────────────
function RecallBanner({ recalls, vehicleLabel }) {
  const [open, setOpen] = useState(false);
  const count = recalls.length;
  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", borderRadius: open ? "12px 12px 0 0" : 12,
        border: "1.5px solid #fecaca", background: "#fef2f2",
        cursor: "pointer", textAlign: "left",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🚨</span>
          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>
              {count} Open Recall{count > 1 ? "s" : ""} Found
            </span>
            <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: 8 }}>
              NHTSA safety data for {vehicleLabel}
            </span>
          </div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#dc2626", background: "#fee2e2", padding: "4px 10px", borderRadius: 20, flexShrink: 0 }}>
          {open ? "Hide ▲" : `View ${count} Recall${count > 1 ? "s" : ""} ▼`}
        </span>
      </button>
      {open && (
        <div style={{ border: "1.5px solid #fecaca", borderTop: "none", borderRadius: "0 0 12px 12px", padding: "14px 16px", background: "#fff" }}>
          {recalls.slice(0, 5).map((r, i) => <RecallCard key={i} recall={r} />)}
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
            Source: NHTSA — National Highway Traffic Safety Administration
          </div>
        </div>
      )}
    </div>
  );
}

export default function AutoSearch() {
  const [year, setYear]     = useState("");
  const [make, setMake]     = useState("");
  const [model, setModel]   = useState("");
  const [problems, setProblems] = useState(new Set());
  const [problem, setProblem] = useState("");
  const [customProblem, setCustomProblem] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const [prices, setPrices]   = useState(null);
  const [videos, setVideos]   = useState(null);
  const [recalls, setRecalls] = useState(null);
  const [partsUrls, setPartsUrls] = useState(null);

  const hasOther = problems.has("Other");
  const effectiveProblem = [...problems].filter(p => p !== "Other").concat(hasOther && customProblem ? [customProblem] : []).join(", ");
  const canSearch = year && make && model && (problems.size > 0) && effectiveProblem;

  const search = async () => {
    if (!canSearch) return;
    setLoading(true); setError(null);
    setPrices(null); setVideos(null); setRecalls(null); setPartsUrls(null);

    try {
      // Run all 4 in parallel
      const [priceData, videoData, recallData] = await Promise.allSettled([
        callAgent(pricePrompt(year, make, model, effectiveProblem)),
        searchYouTube(year, make, model, effectiveProblem),
        fetchRecalls(year, make, model),
      ]);

      if (priceData.status === "fulfilled") setPrices(priceData.value?.results || []);
      if (videoData.status === "fulfilled") setVideos(videoData.value?.items || []);
      if (recallData.status === "fulfilled") setRecalls(recallData.value?.results || []);
      setPartsUrls(getPartsUrls(year, make, model, effectiveProblem));

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const vehicleLabel = year && make && model ? `${year} ${make} ${model}` : null;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 16px 40px", fontFamily: "system-ui, -apple-system, sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 20, padding: "16px 20px", background: "linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 100%)", borderRadius: 16, color: "#fff" }}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>🚗 Auto Repair Helper</div>
        <div style={{ fontSize: 13, opacity: 0.85 }}>Before paying the dealer — see DIY fixes, YouTube tutorials, fair prices and open recalls</div>
      </div>

      {/* Vehicle Selector */}
      <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #e5e7eb", padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
        {/* Vehicle Card */}
        <div style={{ background: "#f8fafc", borderRadius: 12, border: "1.5px solid #e2e8f0", padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>🚗 Your Vehicle</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          {/* Year */}
          <div style={fld}>
            <label style={lbl}>Year</label>
            <select value={year} onChange={e => { setYear(e.target.value); setMake(""); setModel(""); }} style={inp}>
              <option value="">Select year</option>
              {VEHICLE_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {/* Make */}
          <div style={fld}>
            <label style={lbl}>Make</label>
            <select value={make} onChange={e => { setMake(e.target.value); setModel(""); }}
              style={{ ...inp, opacity: year ? 1 : 0.5 }} disabled={!year}>
              <option value="">{year ? "Select make" : "Select year first"}</option>
              {VEHICLE_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {/* Model */}
          <div style={fld}>
            <label style={lbl}>Model</label>
            <select value={model} onChange={e => setModel(e.target.value)}
              style={{ ...inp, opacity: make ? 1 : 0.5 }} disabled={!make}>
              <option value="">{make ? "Select model" : "Select make first"}</option>
              {(VEHICLE_DATA[make] || []).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        {/* Problem Card */}
        <div style={{ background: "#f8fafc", borderRadius: 12, border: "1.5px solid #e2e8f0", padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>🔧 What's the Problem?</div>

        {/* Problem Selector — Accordion */}
        <ProblemAccordion problems={problems} setProblems={setProblems} />
        {problems.has("Other") && (
          <input value={customProblem} onChange={e => setCustomProblem(e.target.value)}
            placeholder="e.g. Strange noise when turning left at low speed"
            style={{ ...inp, marginTop: 8 }} />
        )}

        </div>
        </div>

        <button onClick={search} disabled={!canSearch || loading}
          style={{ width: "100%", marginTop: 16, padding: 13, borderRadius: 12, border: "none", background: !canSearch ? "#e5e7eb" : loading ? "#93c5fd" : "#1d4ed8", color: !canSearch ? "#9ca3af" : "#fff", fontSize: 15, fontWeight: 700, cursor: !canSearch || loading ? "not-allowed" : "pointer" }}>
  {loading ? "Finding fixes..." : canSearch ? `Search fixes for ${vehicleLabel}` : "Select vehicle and problem to search"}
        </button>
      </div>

      {error && <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 12, padding: "14px 18px", marginBottom: 16, color: "#dc2626", fontSize: 14 }}>⚠️ {error}</div>}

      {/* RESULTS */}
      {(prices || videos || recalls || partsUrls) && (
        <div>
          {/* Recalls — compact collapsible banner */}
          {recalls?.length > 0 && <RecallBanner recalls={recalls} vehicleLabel={vehicleLabel} />}

          {/* Price Estimator */}
          {prices?.length > 0 && (
            <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #e5e7eb", padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
              <SectionHeader icon="💰" title="Cost Comparison" subtitle="Cheapest fix first — always try DIY or budget options before the dealer" />
              {prices.map((r, i) => <PriceCard key={i} r={r} rank={i} />)}
              <div style={{ marginTop: 8, padding: "10px 14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, fontSize: 12, color: "#15803d" }}>
                💡 MioPrice always shows the cheapest fix first — even if it means $0 for us. Your savings matter more.
              </div>
            </div>
          )}

          {/* Parts Finder */}
          {partsUrls && (
            <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #e5e7eb", padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
              <SectionHeader icon="🔩" title="Find Parts" subtitle={`Search parts for ${vehicleLabel} across multiple stores`} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {partsUrls.map(site => (
                  <a key={site.name} href={site.url} target="_blank" rel="noopener noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: `1.5px solid ${site.color}33`, background: `${site.color}0d`, color: site.color, fontSize: 13, fontWeight: 700, textDecoration: "none" }}
                    onMouseEnter={e => e.currentTarget.style.background = `${site.color}22`}
                    onMouseLeave={e => e.currentTarget.style.background = `${site.color}0d`}>
                    <span style={{ width: 20, height: 20, borderRadius: 5, background: site.color, color: "#fff", fontSize: 10, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>{site.icon}</span>
                    {site.name}
                  </a>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 10 }}>Compare prices across stores before buying. MioPrice may earn a commission on purchases.</div>
            </div>
          )}

          {/* YouTube Videos */}
          {videos !== null && (
            <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #e5e7eb", padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
              <SectionHeader icon="📺" title="How-To Videos" subtitle={`YouTube tutorials for ${vehicleLabel} — ${effectiveProblem}`} />
              {videos.length > 0 ? (
                videos.map((v, i) => <VideoCard key={i} video={v} />)
              ) : (
                <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No videos found for this specific search. Try a broader problem description.</div>
              )}
            </div>
          )}

          {/* No recalls — compact green pill */}
          {recalls !== null && recalls.length === 0 && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "#f0fdf4", border: "1.5px solid #bbf7d0", borderRadius: 20, fontSize: 12, color: "#15803d", marginBottom: 16 }}>
              ✓ No open NHTSA recalls for your {vehicleLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
